import React from 'react';
import { CheckIcon, PencilIcon, TrashIcon, NoSymbolIcon, ArrowPathIcon, DocumentTextIcon, ArchiveBoxIcon, PlayIcon, ListBulletIcon } from '@heroicons/react/24/outline';

const SongsTable = ({ songs, onEdit, onDelete, onViewChart, onAddToSetlist }) => {
  const handleYouTubeSearch = (artist, title) => {
    const searchQuery = encodeURIComponent(`${artist} ${title}`);
    const youtubeUrl = `https://www.youtube.com/results?search_query=${searchQuery}`;
    window.open(youtubeUrl, '_blank');
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-blue-50">
          <tr>
            <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Artist
            </th>
            <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Song
            </th>
            <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Dur
            </th>
            <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              BPM
            </th>
            <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Key
            </th>
            <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Year
            </th>
            <th className="px-2 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {songs.map((song, index) => (
            <tr key={song.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-2 md:px-6 py-4 whitespace-nowrap">
                {renderStatusIcon(song.status)}
              </td>
              <td className="px-2 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {song.artist}
              </td>
              <td className="px-2 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {song.title}
              </td>
              <td className="hidden md:table-cell px-2 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {song.duration ? formatDuration(song.duration) : '—'}
              </td>
              <td className="hidden md:table-cell px-2 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {song.bpm || '—'}
              </td>
              <td className="hidden md:table-cell px-2 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {song.key || '—'}
              </td>
              <td className="hidden md:table-cell px-2 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {song.year || '—'}
              </td>
              <td className="px-2 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-right space-x-2 inline-block">
                {song.chart && (
                  <button
                    onClick={() => onViewChart(song)}
                    className="text-green-600 hover:text-green-900"
                    title="View Chart"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                  </button>
                )}
                {song.artist !== 'Doulos' && (
                  <button
                    onClick={() => handleYouTubeSearch(song.artist, song.title)}
                    className="text-red-600 hover:text-red-900"
                    title="Search on YouTube"
                  >
                    <PlayIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => onAddToSetlist(song)}
                  className="text-purple-600 hover:text-purple-900"
                  title="Add to Setlist"
                >
                  <ListBulletIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onEdit(song)}
                  className="text-blue-600 hover:text-blue-900"
                  title="Edit"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(song.id)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {songs.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No songs found. Add your first song!
        </div>
      )}
    </div>
  );
};

const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const renderStatusIcon = (status) => {
  switch (status) {
    case 'GOOD':
      return <CheckIcon className="w-5 h-5 text-green-500" title="Good" />;
    case 'IN_PROGRESS':
      return <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" title="In Progress" />;
    case 'TODO':
      return <NoSymbolIcon className="w-5 h-5 text-red-500" title="To Do" />;
    case 'BACKLOG':
      return <ArchiveBoxIcon className="w-5 h-5 text-gray-500" title="Backlog" />;
    default:
      return <CheckIcon className="w-5 h-5 text-green-500" title="Good" />;
  }
};

export default SongsTable;